import { useState, useEffect, useMemo } from 'react';
import { Image, ImageViewer, Loading, Popup } from 'antd-mobile';
import { useTransactions } from '../hooks/useTransactions';
import { ChevronDown, ChevronLeft, ChevronRight, Calendar, Image as ImageIcon } from 'lucide-react';
import { formatCurrency } from '@tangtung/shared';
import './SlipsPage.css';

const THAI_MONTHS = [
    'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน',
    'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'
];

const THAI_MONTHS_SHORT = [
    'ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.',
    'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.'
];

const THAI_DAYS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

export default function SlipsPage() {
    const { transactions, refreshTransactions } = useTransactions();
    const [loading, setLoading] = useState(false);
    const [viewerVisible, setViewerVisible] = useState(false);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // Date range state
    const [showDatePicker, setShowDatePicker] = useState(false);
    const [calendarMonth, setCalendarMonth] = useState(new Date());

    // Default: current month range (1st - today)
    const now = new Date();
    const [startDate, setStartDate] = useState<Date>(new Date(now.getFullYear(), now.getMonth(), 1));
    const [endDate, setEndDate] = useState<Date>(now);

    // Temp selection for picking range in modal
    const [tempStart, setTempStart] = useState<Date | null>(null);
    const [tempEnd, setTempEnd] = useState<Date | null>(null);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            await refreshTransactions();
            setLoading(false);
        };
        load();
    }, [refreshTransactions]);

    const parseDate = (dateStr: string) => {
        return new Date(dateStr.replace(' ', 'T') + 'Z');
    };

    const toDateStr = (d: Date) => d.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });

    const isSameDay = (d1: Date, d2: Date) => {
        return d1.toLocaleDateString('en-CA') === d2.toLocaleDateString('en-CA');
    };

    const isBetween = (d: Date, start: Date, end: Date) => {
        const ds = d.toLocaleDateString('en-CA');
        const ss = start.toLocaleDateString('en-CA');
        const es = end.toLocaleDateString('en-CA');
        return ds >= ss && ds <= es;
    };

    // Format the date range label for the button
    const getRangeLabel = () => {
        const sameMonth = startDate.getMonth() === endDate.getMonth() && startDate.getFullYear() === endDate.getFullYear();
        if (sameMonth) {
            const s = startDate.getDate();
            const e = endDate.getDate();
            return `${s}-${e} ${THAI_MONTHS_SHORT[startDate.getMonth()]}`;
        }
        return `${startDate.getDate()} ${THAI_MONTHS_SHORT[startDate.getMonth()]} - ${endDate.getDate()} ${THAI_MONTHS_SHORT[endDate.getMonth()]}`;
    };

    // Handle calendar day click (range selection)
    const handleDayClick = (date: Date) => {
        if (!tempStart || (tempStart && tempEnd)) {
            // First click or reset: set start
            setTempStart(date);
            setTempEnd(null);
        } else {
            // Second click: set end
            if (date < tempStart) {
                setTempEnd(tempStart);
                setTempStart(date);
            } else {
                setTempEnd(date);
            }
        }
    };

    // Quick select: this month
    const selectThisMonth = () => {
        const s = new Date(now.getFullYear(), now.getMonth(), 1);
        setTempStart(s);
        setTempEnd(now);
    };

    // Quick select: last month
    const selectLastMonth = () => {
        const s = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        const e = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
        setTempStart(s);
        setTempEnd(e);
        setCalendarMonth(s);
    };

    // Quick select: last 7 days
    const selectLast7Days = () => {
        const s = new Date(now);
        s.setDate(s.getDate() - 6);
        setTempStart(s);
        setTempEnd(now);
    };

    // Confirm selection
    const confirmRange = () => {
        if (tempStart) {
            setStartDate(tempStart);
            setEndDate(tempEnd || tempStart);
        }
        setShowDatePicker(false);
    };

    // Open modal
    const openPicker = () => {
        setTempStart(startDate);
        setTempEnd(endDate);
        setCalendarMonth(startDate);
        setShowDatePicker(true);
    };

    // Calendar navigation
    const prevMonth = () => {
        setCalendarMonth(new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1));
    };

    const nextMonth = () => {
        const next = new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1);
        if (next <= new Date(now.getFullYear(), now.getMonth() + 1, 0)) {
            setCalendarMonth(next);
        }
    };

    const getDaysInMonth = (year: number, month: number) => new Date(year, month + 1, 0).getDate();
    const getFirstDayOfMonth = (year: number, month: number) => new Date(year, month, 1).getDay();

    const renderCalendar = () => {
        const year = calendarMonth.getFullYear();
        const month = calendarMonth.getMonth();
        const daysInMonth = getDaysInMonth(year, month);
        const firstDay = getFirstDayOfMonth(year, month);
        const today = new Date();

        const days = [];

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(year, month, day);
            const isTodayDate = isSameDay(date, today);
            const isFuture = date > today;

            const isStart = tempStart && isSameDay(date, tempStart);
            const isEnd = tempEnd && isSameDay(date, tempEnd);
            const isInRange = tempStart && tempEnd && isBetween(date, tempStart, tempEnd);
            const isSelected = isStart || isEnd;

            let cls = 'calendar-day';
            if (isTodayDate) cls += ' today';
            if (isSelected) cls += ' selected';
            if (isInRange && !isSelected) cls += ' in-range';
            if (isFuture) cls += ' disabled';

            days.push(
                <button
                    key={day}
                    className={cls}
                    disabled={isFuture}
                    onClick={() => !isFuture && handleDayClick(date)}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    // Filter slips by date range
    const slips = useMemo(() => {
        const startStr = toDateStr(startDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        const endStr = toDateStr(end);

        return transactions
            .filter(tx => tx.slip_url)
            .filter(tx => {
                const utcDateStr = tx.created_at.replace(' ', 'T') + 'Z';
                const txDate = new Date(utcDateStr);
                const txDateStr = txDate.toLocaleDateString('en-CA', { timeZone: 'Asia/Bangkok' });
                return txDateStr >= startStr && txDateStr <= endStr;
            })
            .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }, [transactions, startDate, endDate]);

    const viewerImages = slips.map(tx => tx.slip_url as string);

    const handleImageClick = (index: number) => {
        setCurrentImageIndex(index);
        setViewerVisible(true);
    };

    // Selection hint text
    const getSelectionHint = () => {
        if (!tempStart) return 'กดเลือกวันเริ่มต้น';
        if (!tempEnd) return `${tempStart.getDate()} ${THAI_MONTHS_SHORT[tempStart.getMonth()]} → กดเลือกวันสิ้นสุด`;
        return `${tempStart.getDate()} ${THAI_MONTHS_SHORT[tempStart.getMonth()]} - ${tempEnd.getDate()} ${THAI_MONTHS_SHORT[tempEnd.getMonth()]}`;
    };

    return (
        <div className="page slips-page">
            <div className="custom-header">
                <div className="header-left"></div>
                <div className="header-center">
                    <h1>อัลบั้มสลิป</h1>
                </div>
                <div className="header-right">
                    <button className="date-picker-btn-slips" onClick={openPicker}>
                        <Calendar size={14} />
                        <span>{getRangeLabel()}</span>
                        <ChevronDown size={14} />
                    </button>
                </div>
            </div>

            <div className="page-content">
                {loading ? (
                    <div className="loading-state">
                        <Loading color="primary" />
                        <span>กำลังโหลด...</span>
                    </div>
                ) : slips.length === 0 ? (
                    <div className="empty-state-slips warning-mode">
                        <img
                            src="/Slimp.png"
                            alt="No transactions"
                            className="empty-state-mascot"
                            style={{ width: '160px', height: '160px', objectFit: 'contain', marginBottom: '10px' }}
                        />
                        <h3>ไม่มีสลิปในช่วงนี้</h3>
                        <p>สลิปที่ถ่ายไว้จะปรากฏที่นี่</p>
                    </div>
                ) : (
                    <div className="slips-grid">
                        {slips.map((tx, index) => (
                            <div key={tx.id} className="slip-item" onClick={() => handleImageClick(index)}>
                                <Image
                                    src={tx.slip_url || ''}
                                    className="slip-thumbnail"
                                    fit="cover"
                                    lazy
                                />
                                <div className="slip-info-overlay">
                                    <span className="slip-amount">{formatCurrency(tx.amount)}</span>
                                    <span className="slip-date">
                                        {parseDate(tx.created_at).toLocaleDateString('th-TH', { day: 'numeric' })}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <ImageViewer.Multi
                images={viewerImages}
                visible={viewerVisible}
                defaultIndex={currentImageIndex}
                onClose={() => setViewerVisible(false)}
            />

            {/* Date Range Picker Popup */}
            <Popup
                visible={showDatePicker}
                onMaskClick={() => setShowDatePicker(false)}
                bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20 }}
            >
                <div className="date-picker-modal">
                    <div className="date-picker-header">
                        <h3>เลือกช่วงวันที่</h3>
                        <button className="close-btn" onClick={() => setShowDatePicker(false)}>✕</button>
                    </div>

                    {/* Quick Select */}
                    <div className="quick-select">
                        <button className="quick-btn" onClick={selectLast7Days}>7 วัน</button>
                        <button className="quick-btn" onClick={selectThisMonth}>เดือนนี้</button>
                        <button className="quick-btn" onClick={selectLastMonth}>เดือนก่อน</button>
                    </div>

                    {/* Calendar */}
                    <div className="mini-calendar">
                        <div className="calendar-header">
                            <button className="nav-btn" onClick={prevMonth}><ChevronLeft size={20} /></button>
                            <span className="month-year">{THAI_MONTHS[calendarMonth.getMonth()]} {calendarMonth.getFullYear() + 543}</span>
                            <button className="nav-btn" onClick={nextMonth}><ChevronRight size={20} /></button>
                        </div>
                        <div className="calendar-weekdays">
                            {THAI_DAYS.map((day) => (<div key={day} className="weekday">{day}</div>))}
                        </div>
                        <div className="calendar-days">
                            {renderCalendar()}
                        </div>
                    </div>

                    {/* Selection hint & confirm */}
                    <div className="range-footer">
                        <div className="range-hint">{getSelectionHint()}</div>
                        <button
                            className="confirm-btn-range"
                            disabled={!tempStart}
                            onClick={confirmRange}
                        >
                            ตกลง
                        </button>
                    </div>
                </div>
            </Popup>
        </div>
    );
}
